import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LockIcon from "@mui/icons-material/Lock";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  agruparPasosEnPestanas,
  indicePestanaActiva,
  indiceSubtabActiva,
  PORTAL_PESTANAS_TRAMITE,
  type PortalPestanaVm,
  type PortalSubtabVm,
} from "../../config/portalTrazabilidadEstructura";

export type PortalDocItem = {
  tipo: string;
  etiqueta: string;
  etapa_key: string;
  extensiones: string[];
  estado: string;
  puede_cargar: boolean;
  bloqueado: boolean;
  observacion?: string;
  cargado_at?: string | null;
};

export type PortalPaso = {
  id: string;
  bloque: string;
  titulo: string;
  descripcion: string;
  estado_paso: "completado" | "activo" | "en_espera_revision" | "pendiente";
  documentos: PortalDocItem[];
  observaciones?: string;
  ayuda?: string;
  orden: number;
};

const acceptDesdeExtensiones = (exts: string[]) =>
  exts.map((e) => `.${String(e || "").replace(/^\./, "").toLowerCase()}`).join(",");

const etiquetaEstadoPaso = (estado: string) => {
  switch (estado) {
    case "completado":
      return { label: "Completado", color: "success" as const };
    case "activo":
      return { label: "Acción requerida", color: "warning" as const };
    case "en_espera_revision":
      return { label: "En revisión EMCALI", color: "info" as const };
    default:
      return { label: "Pendiente", color: "default" as const };
  }
};

const etiquetaEstadoDoc = (doc: PortalDocItem) => {
  if (doc.puede_cargar) return { label: "Pendiente de carga", color: "warning" as const };
  if (doc.estado === "rechazado") return { label: "Rechazado — debe corregir", color: "error" as const };
  if (doc.bloqueado || doc.estado === "cargado_en_revision")
    return { label: "Cargado — bloqueado", color: "success" as const };
  if (doc.estado === "cargado_aprobado") return { label: "Aprobado", color: "success" as const };
  return { label: "—", color: "default" as const };
};

function ContenidoPaso({
  paso,
  archivosPorTipo,
  subiendoTipo,
  onArchivoChange,
  onSubir,
  docRefs,
  pasoRefs,
}: {
  paso: PortalPaso;
  archivosPorTipo: Record<string, File | null>;
  subiendoTipo: string | null;
  onArchivoChange: (tipo: string, file: File | null) => void;
  onSubir: (doc: PortalDocItem) => void;
  docRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  pasoRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}) {
  const esActivo = paso.estado_paso === "activo";
  const chipPaso = etiquetaEstadoPaso(paso.estado_paso);

  return (
    <Box
      ref={(el: HTMLDivElement | null) => {
        pasoRefs.current[paso.id] = el;
      }}
      sx={{ py: 1 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {paso.titulo}
        </Typography>
        <Chip size="small" label={chipPaso.label} color={chipPaso.color} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {paso.descripcion}
      </Typography>
      {paso.ayuda ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          {paso.ayuda}
        </Alert>
      ) : null}
      {paso.observaciones ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {paso.observaciones}
        </Alert>
      ) : null}

      {paso.documentos.length > 0 ? (
        <Stack spacing={1.5}>
          {paso.documentos.map((doc) => {
            const chipDoc = etiquetaEstadoDoc(doc);
            const puedeSubir = doc.puede_cargar && !doc.bloqueado;
            return (
              <Paper
                key={doc.tipo}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: esActivo && puedeSubir ? "warning.main" : undefined,
                  bgcolor: doc.bloqueado ? "action.hover" : undefined,
                }}
                ref={(el: HTMLDivElement | null) => {
                  docRefs.current[doc.tipo] = el;
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Typography fontWeight={700}>{doc.etiqueta}</Typography>
                  <Chip size="small" label={chipDoc.label} color={chipDoc.color} />
                </Stack>
                {doc.observacion ? (
                  <Typography variant="caption" color="error" display="block" sx={{ mb: 0.5 }}>
                    Observación: {doc.observacion}
                  </Typography>
                ) : null}
                {doc.cargado_at ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Última carga: {new Date(doc.cargado_at).toLocaleString()}
                  </Typography>
                ) : null}

                {puedeSubir ? (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <TextField
                      type="file"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ accept: acceptDesdeExtensiones(doc.extensiones) }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onArchivoChange(doc.tipo, e.target.files?.[0] || null);
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onSubir(doc)}
                      disabled={subiendoTipo !== null}
                    >
                      {subiendoTipo === doc.tipo ? "Subiendo…" : "Subir archivo"}
                    </Button>
                  </Stack>
                ) : doc.bloqueado || doc.estado.startsWith("cargado") ? (
                  <Alert severity="success" icon={<LockIcon fontSize="small" />}>
                    Documento ya enviado. No puede volver a cargarlo hasta nueva indicación del revisor.
                  </Alert>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Aún no está autorizado para cargar este documento.
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Stack>
      ) : paso.estado_paso === "en_espera_revision" ? (
        <Alert severity="info">EMCALI está revisando esta etapa. No requiere carga por ahora.</Alert>
      ) : paso.estado_paso === "completado" ? (
        <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />}>
          Etapa completada desde su lado.
        </Alert>
      ) : null}
    </Box>
  );
}

type Props = {
  pasos: PortalPaso[];
  pestanasApi?: any[];
  pasoActivoId: string | null;
  pestanaActivaId?: string | null;
  archivosPorTipo: Record<string, File | null>;
  subiendoTipo: string | null;
  onArchivoChange: (tipo: string, file: File | null) => void;
  onSubir: (doc: PortalDocItem) => void;
  pasoRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  docRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  correccionDatos?: {
    campos: Record<string, any>;
    labels: Record<string, string>;
    onCampoChange: (key: string, value: string) => void;
    onGuardar: () => void;
    guardando: boolean;
    seccionRef?: React.RefObject<HTMLDivElement | null>;
  } | null;
};

export default function PortalSolicitanteTrazabilidad({
  pasos,
  pestanasApi,
  pasoActivoId,
  pestanaActivaId,
  archivosPorTipo,
  subiendoTipo,
  onArchivoChange,
  onSubir,
  pasoRefs,
  docRefs,
  correccionDatos,
}: Props) {
  const pestanas = React.useMemo((): PortalPestanaVm[] => {
    if (Array.isArray(pestanasApi) && pestanasApi.length) {
      return pestanasApi.map(
        (p: {
          id: string;
          label: string;
          estado_pestana?: string;
          subtabs?: Array<{ id: string; label: string; pasos?: PortalPaso[] }>;
        }) => ({
          id: p.id,
          label: p.label,
          estado_pestana: p.estado_pestana || "pendiente",
          subtabs: (p.subtabs || []).map((st) => ({
            id: st.id,
            label: st.label,
            pasos: (st.pasos || []) as PortalPaso[],
          })),
        }),
      );
    }
    return agruparPasosEnPestanas(pasos, PORTAL_PESTANAS_TRAMITE);
  }, [pasos, pestanasApi]);

  const tabInicial = React.useMemo(() => {
    if (pestanaActivaId) {
      const i = pestanas.findIndex((p) => p.id === pestanaActivaId);
      if (i >= 0) return i;
    }
    if (pasoActivoId) {
      const i = pestanas.findIndex((p) =>
        p.subtabs.some((st: PortalSubtabVm) =>
          st.pasos.some((paso: PortalPaso) => paso.id === pasoActivoId),
        ),
      );
      if (i >= 0) return i;
    }
    return indicePestanaActiva(pestanas);
  }, [pestanas, pestanaActivaId, pasoActivoId]);

  const [tabPrincipal, setTabPrincipal] = React.useState(tabInicial);
  const [subtabPorPestana, setSubtabPorPestana] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    setTabPrincipal(tabInicial);
  }, [tabInicial, pasos.length]);

  React.useEffect(() => {
    const pestana = pestanas[tabPrincipal];
    if (!pestana) return;
    const idx = indiceSubtabActiva(pestana.subtabs);
    setSubtabPorPestana((prev) => ({ ...prev, [pestana.id]: idx }));
  }, [tabPrincipal, pestanas]);

  if (!pestanas.length) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No hay etapas registradas para su trámite en este momento.
      </Alert>
    );
  }

  const pestanaActual = pestanas[tabPrincipal] || pestanas[0];
  const subtabIdx = subtabPorPestana[pestanaActual.id] ?? 0;
  const subtabActual = pestanaActual.subtabs[subtabIdx] || pestanaActual.subtabs[0];

  const iconoPestana = (estado: string) => {
    if (estado === "completado") return <CheckCircleIcon fontSize="small" color="success" />;
    if (estado === "activo") return <UploadFileIcon fontSize="small" color="warning" />;
    return <HourglassEmptyIcon fontSize="small" color="disabled" />;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Avance de su trámite
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Navegue por las mismas etapas que en la gestión interna. Solo la subpestaña con acción requerida permite
        cargar archivos nuevos.
      </Typography>

      <Tabs
        value={tabPrincipal}
        onChange={(_, v) => setTabPrincipal(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}
      >
        {pestanas.map((p, i) => {
          const chip = etiquetaEstadoPaso(p.estado_pestana);
          return (
            <Tab
              key={p.id}
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {iconoPestana(p.estado_pestana)}
                  <span>{p.label}</span>
                  <Chip size="small" label={chip.label} color={chip.color} sx={{ ml: 0.5, height: 20 }} />
                </Stack>
              }
              value={i}
            />
          );
        })}
      </Tabs>

      {pestanaActual.subtabs.length > 1 ? (
        <Tabs
          value={subtabIdx}
          onChange={(_, v) => setSubtabPorPestana((prev) => ({ ...prev, [pestanaActual.id]: v }))}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 1, minHeight: 40 }}
        >
          {pestanaActual.subtabs.map((st: PortalSubtabVm, i: number) => {
            const activa = st.pasos.some((p: PortalPaso) => p.estado_paso === "activo");
            return (
              <Tab
                key={st.id}
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {activa ? <UploadFileIcon sx={{ fontSize: 16 }} color="warning" /> : null}
                    <span>{st.label}</span>
                  </Stack>
                }
                value={i}
              />
            );
          })}
        </Tabs>
      ) : (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: "text.secondary" }}>
          {subtabActual?.label}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa" }}>
        {pestanaActual.id === "solicitud" &&
        subtabActual?.id === "revision" &&
        correccionDatos &&
        Object.keys(correccionDatos.campos).length > 0 ? (
          <Box ref={correccionDatos.seccionRef}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Corrección de datos del formulario
            </Typography>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Complete solo los campos habilitados por el revisor y envíe los cambios.
            </Alert>
            <Stack spacing={1.2}>
              {Object.keys(correccionDatos.campos).map((key) => (
                <TextField
                  key={key}
                  label={correccionDatos.labels[key] || key}
                  value={correccionDatos.campos[key] ?? ""}
                  onChange={(e) => correccionDatos.onCampoChange(key, e.target.value)}
                  fullWidth
                  size="small"
                />
              ))}
            </Stack>
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              color="success"
              onClick={correccionDatos.onGuardar}
              disabled={correccionDatos.guardando}
            >
              Enviar correcciones de datos
            </Button>
            {subtabActual.pasos.length > 0 ? <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }} /> : null}
          </Box>
        ) : null}

        {subtabActual?.pasos.map((paso: PortalPaso) => (
          <ContenidoPaso
            key={paso.id}
            paso={paso}
            archivosPorTipo={archivosPorTipo}
            subiendoTipo={subiendoTipo}
            onArchivoChange={onArchivoChange}
            onSubir={onSubir}
            docRefs={docRefs}
            pasoRefs={pasoRefs}
          />
        ))}
      </Paper>
    </Box>
  );
}
